import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import axios from '../../api/axios';
import { Alert, CircularProgress } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import 'bootstrap/dist/css/bootstrap.min.css';
import AuthPageLayout from './AuthPageLayout';

const FORGOT_PASSWORD_URL = '/Registration/ForgotPassword';

const ForgotPassword = () => {
    const { t } = useTranslation();
    const emailRef = useRef();
    const errRef = useRef();

    const [email, setEmail] = useState('');
    const [errMsg, setErrMsg] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    useEffect(() => {
        if (emailRef.current) {
            emailRef.current.focus();
        }
    }, []);

    useEffect(() => {
        setErrMsg('');
    }, [email]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrMsg('');

        try {
            await axios.post(
                FORGOT_PASSWORD_URL,
                { email },
                {
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            setIsSubmitted(true);
        } catch (err) {
            if (!err?.response) {
                setErrMsg(t('errorNoServerResponse'));
            } else {
                setErrMsg(t('forgotPasswordError'));
            }
            if (errRef.current) {
                errRef.current.focus();
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (isSubmitted) {
        return (
            <AuthPageLayout>
                <div
                    className="card shadow"
                    style={{ width: '100%', maxWidth: '450px', border: 'none', borderRadius: '0.5rem' }}
                >
                    <div className="card-body text-center p-4">
                        <Link to="/Home">
                            <img
                                src="/assets/img/OnlineCADCOM_FullLogo.png"
                                alt="Online-CADCOM"
                                style={{ height: '50px', marginBottom: '16px' }}
                            />
                        </Link>
                        <CheckCircleIcon sx={{ fontSize: 60, color: '#28a745', mb: 2 }} />
                        <h3 style={{ color: '#333' }}>{t('forgotPasswordEmailSentTitle')}</h3>
                        <p className="text-muted mb-4">{t('forgotPasswordEmailSentMessage')}</p>
                        <p className="text-muted small mb-4">{t('forgotPasswordCheckSpam')}</p>
                        <Link
                            to="/login"
                            className="btn btn-lg w-100"
                            style={{ backgroundColor: '#112d4e', color: '#fff', border: 'none' }}
                        >
                            {t('backToLogin')}
                        </Link>
                    </div>
                </div>
            </AuthPageLayout>
        );
    }

    return (
        <AuthPageLayout>
            <div
                className="card shadow"
                style={{ width: '100%', maxWidth: '400px', border: 'none', borderRadius: '0.5rem' }}
            >
                <div className="card-body">
                    <div className="text-center mb-4">
                        <Link to="/Home">
                            <img
                                src="/assets/img/OnlineCADCOM_FullLogo.png"
                                alt="Online-CADCOM"
                                style={{ height: '60px' }}
                            />
                        </Link>
                        <p className="text-muted mt-3">{t("forgotPasswordSubtitle")}</p>
                    </div>

                    {errMsg && (
                        <Alert
                            ref={errRef}
                            variant="filled"
                            severity="error"
                            className="mb-3"
                            tabIndex={-1}
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => setErrMsg('')}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                        >
                            {errMsg}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="form-floating mb-3">
                            <input
                                type="email"
                                className="form-control"
                                id="email"
                                placeholder={t("forgotPasswordEmail")}
                                ref={emailRef}
                                onChange={(e) => setEmail(e.target.value)}
                                value={email}
                                required
                                autoComplete="email"
                            />
                            <label htmlFor="email">{t("forgotPasswordEmail")}</label>
                        </div>
                        <button
                            className="btn btn-lg w-100"
                            type="submit"
                            disabled={isLoading}
                            style={{ backgroundColor: '#112d4e', color: '#fff', border: 'none' }}
                        >
                            {isLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                t("forgotPasswordSubmitButton")
                            )}
                        </button>
                    </form>

                    <div className="text-center mt-3">
                        <Link to="/login" style={{ color: '#112d4e', textDecoration: 'none' }}>
                            <ArrowBackIcon fontSize="small" className="me-1" />
                            {t('backToLogin')}
                        </Link>
                    </div>
                </div>
            </div>
        </AuthPageLayout>
    );
};

export default ForgotPassword;
